import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowLeft } from "lucide-react";
import { Wordmark } from "@/components/Logo";
import { api, errMsg } from "@/lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { setErr("The two passwords don't match."); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
    } catch (e2) { setErr(errMsg(e2)); } finally { setBusy(false); }
  };

  return (
    <main data-testid="reset-page" className="min-h-screen relative overflow-hidden px-5 sm:px-10 py-8">
      <div className="absolute top-0 right-0 w-[520px] h-[520px] rounded-full bg-[var(--blood)]/15 blur-[140px] pointer-events-none" />
      <div className="relative max-w-6xl mx-auto">
        <Link to="/" data-testid="reset-home-link"><Wordmark /></Link>
        <div className="grid place-items-center py-16 sm:py-24">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="w-full max-w-md border border-[var(--gold)]/40 bg-[var(--crypt)] p-8 sm:p-10 hex-clip">
            {done ? (
              <div data-testid="reset-success" className="text-center">
                <CheckCircle2 className="w-10 h-10 text-[var(--gold)] mx-auto" />
                <h1 className="font-display text-3xl font-bold mt-6">Password updated</h1>
                <p className="text-sm text-[var(--ink-dim)] mt-3">You can now log in with your new password.</p>
                <Link to="/account" data-testid="reset-login-link" className="btn-blood block mt-8 py-4 font-mono text-sm tracking-[0.2em] uppercase">Log in</Link>
              </div>
            ) : !token ? (
              <div data-testid="reset-missing-token">
                <h1 className="font-display text-3xl font-bold">Link not valid</h1>
                <p className="text-sm text-[var(--ink-dim)] mt-3">Open the link from your reset email, or request a new one from the login page.</p>
                <Link to="/account" className="btn-ghost block text-center mt-8 py-4 font-mono text-sm tracking-[0.2em] uppercase">Go to login</Link>
              </div>
            ) : (
              <>
                <h1 className="font-display text-3xl font-bold">Choose a new password</h1>
                <p className="text-sm text-[var(--ink-dim)] mt-2">At least 8 characters.</p>
                <form onSubmit={submit} data-testid="reset-form" className="mt-6 space-y-3">
                  <input required minLength={8} type="password" data-testid="reset-password-input" className="field" placeholder="New password" value={pw} onChange={(e) => setPw(e.target.value)} />
                  <input required minLength={8} type="password" data-testid="reset-password-confirm-input" className="field" placeholder="Repeat new password" value={pw2} onChange={(e) => setPw2(e.target.value)} />
                  {err && <p data-testid="reset-error" className="text-sm text-[var(--blood)]">{err}</p>}
                  <button disabled={busy} data-testid="reset-submit-btn" className="btn-blood w-full py-4 font-mono text-sm tracking-[0.2em] uppercase disabled:opacity-60">
                    {busy ? "Saving…" : "Save new password"}
                  </button>
                </form>
              </>
            )}
          </motion.div>
          <Link to="/account" className="mt-8 flex items-center gap-2 text-sm text-[var(--ink-dim)] hover:text-[var(--gold)]"><ArrowLeft className="w-4 h-4" /> Back to login</Link>
        </div>
      </div>
    </main>
  );
}
